/* Texturausrichtung: reine Funktionen auf Brush-Flächen (Valve-220-Achsen). */
import type { Brush, Doc, Face, Vec3 } from './types.ts';
import type { FaceRef } from './find.ts';
import Core from '../core.ts';
import I18n from '../i18n.ts';
/** Teil der Flächenattribute, der geändert werden soll */
export type FacePatch = Partial<Face>;
/** Aufgenommene Flächenattribute (Pinsel) */
export interface Picked { tex: string; ua: Vec3; uo: number; va: Vec3; vo: number; xs: number; ys: number; rot: number }
export type Edge = 'left' | 'right' | 'top' | 'bottom' | 'hcenter' | 'vcenter';
type Axes = { ua: Vec3; va: Vec3 };

const TexAlign = (function (Core) {
  'use strict';
  const _ = I18n.t;
  const { dot, cross, sub, add, mul, norm } = Core;

  /** v um die Einheitsachse k um deg Grad drehen (Rodrigues). */
  function rotAbout(v: Vec3, k: Vec3, deg: number): Vec3 {
    const a = deg * Math.PI / 180, c = Math.cos(a), s = Math.sin(a);
    return add(add(mul(v, c), mul(cross(k, v), s)), mul(k, dot(k, v) * (1 - c)));
  }
  const r4 = (x: number): number => Math.round(x * 10000) / 10000;
  const rv = (v: Vec3): Vec3 => [r4(v[0]), r4(v[1]), r4(v[2])];
  const faceNormal = (b: Brush, fi: number): Vec3 => Core.faceNormal(b.verts, b.faces[fi].vi);
  const facePts = (b: Brush, fi: number): Vec3[] => b.faces[fi].vi.map((i) => b.verts[i]);

  /** Achsen in die Flächenebene projizieren (falls sie sich verirrt haben). */
  function inPlane(f: Face, n: Vec3): Axes {
    const fix = (a: Vec3): Vec3 | null => { const p = sub(a, mul(n, dot(a, n))); return Core.len(p) < 1e-6 ? null : norm(p); };
    let ua = fix(f.ua), va = fix(f.va);
    if (!ua || !va || Math.abs(dot(ua, va)) > 0.999) { const d = Core.defaultAxes(n); ua = fix(d.ua); va = fix(d.va); }
    return { ua: ua as Vec3, va: va as Vec3 };
  }

  /** Auf Weltachsen zurücksetzen (Skala bleibt, Versatz 0). */
  function reset(b: Brush, fi: number): FacePatch {
    const f = b.faces[fi], d = Core.defaultAxes(faceNormal(b, fi));
    return { ua: d.ua, va: d.va, uo: 0, vo: 0, rot: 0, xs: f.xs, ys: f.ys };
  }
  /** Textur tx × ty mal über die Fläche spannen (tw, th = Texturgröße in Pixeln). */
  function fit(b: Brush, fi: number, tx: number, ty: number, tw: number, th: number): FacePatch | null {
    const f = b.faces[fi], n = faceNormal(b, fi), ax = inPlane(f, n), P = facePts(b, fi);
    const su = P.map((p) => dot(p, ax.ua)), sv = P.map((p) => dot(p, ax.va));
    const u0 = Math.min.apply(null, su), u1 = Math.max.apply(null, su), v0 = Math.min.apply(null, sv), v1 = Math.max.apply(null, sv);
    if (u1 - u0 < 0.01 || v1 - v0 < 0.01) return null;
    const xs = (u1 - u0) / (tx * tw), ys = (v1 - v0) / (ty * th);
    return { ua: rv(ax.ua), va: rv(ax.va), xs: r4(xs), ys: r4(ys), uo: r4(-u0 / xs), vo: r4(-v0 / ys), rot: 0 };
  }
  /** Um die Flächennormale drehen (Grad). */
  function rotate(b: Brush, fi: number, deg: number): FacePatch {
    const f = b.faces[fi], n = faceNormal(b, fi), ax = inPlane(f, n);
    return { ua: rv(rotAbout(ax.ua, n, deg)), va: rv(rotAbout(ax.va, n, deg)) };
  }
  function flipU(b: Brush, fi: number): FacePatch { const f = b.faces[fi]; return { ua: f.ua.map((x) => -x) as Vec3, uo: -f.uo }; }
  function flipV(b: Brush, fi: number): FacePatch { const f = b.faces[fi]; return { va: f.va.map((x) => -x) as Vec3, vo: -f.vo }; }
  function shift(b: Brush, fi: number, du: number, dv: number): FacePatch { const f = b.faces[fi]; return { uo: r4(f.uo + du), vo: r4(f.vo + dv) }; }
  function scale(b: Brush, fi: number, k: number): FacePatch { const f = b.faces[fi]; return { xs: r4(f.xs * k), ys: r4(f.ys * k) }; }
  /** Flächen mit der Textur auf eine "natürliche" Größe zentrieren: Versatz so, dass Ecke min auf 0 liegt. */
  function snapOrigin(b: Brush, fi: number): FacePatch {
    const f = b.faces[fi], n = faceNormal(b, fi), ax = inPlane(f, n), P = facePts(b, fi);
    const su = Math.min.apply(null, P.map((p) => dot(p, ax.ua))), sv = Math.min.apply(null, P.map((p) => dot(p, ax.va)));
    return { ua: rv(ax.ua), va: rv(ax.va), uo: r4(-su / f.xs), vo: r4(-sv / f.ys) };
  }

  /** Rest von x modulo m, immer im Bereich [0, m). */
  const wrap = (x: number, m: number): number => { const r = x - Math.floor(x / m) * m; return r > m - 1e-6 ? 0 : r; };
  /** Textur an einer Kante der Fläche ausrichten: edge 'left'/'right' (U-Richtung) oder 'top'/'bottom' (V-Richtung).
   *  Die Kante liegt danach auf einer Kachelgrenze der Textur (tw, th = Texturgröße in Pixeln). Links/oben = kleinere U/V-Werte.
   *  'hcenter'/'vcenter': die Flächenmitte in U- bzw. V-Richtung liegt auf der Kachelmitte; kombinierbar mit der anderen Achse
   *  (erst 'hcenter', dann 'top' ergibt oben mittig ausgerichtet). */
  function alignEdge(b: Brush, fi: number, edge: Edge, tw: number, th: number): FacePatch | null {
    const f = b.faces[fi], n = faceNormal(b, fi), ax = inPlane(f, n), P = facePts(b, fi);
    const S = P.map((p) => dot(p, ax.ua) / f.xs + f.uo), T = P.map((p) => dot(p, ax.va) / f.ys + f.vo);
    const out: FacePatch = { ua: rv(ax.ua), va: rv(ax.va) };
    if (edge === 'left') out.uo = r4(f.uo - wrap(Math.min.apply(null, S), tw));
    else if (edge === 'right') out.uo = r4(f.uo - wrap(Math.max.apply(null, S), tw));
    else if (edge === 'top') out.vo = r4(f.vo - wrap(Math.min.apply(null, T), th));
    else if (edge === 'bottom') out.vo = r4(f.vo - wrap(Math.max.apply(null, T), th));
    else if (edge === 'hcenter') out.uo = r4(f.uo - wrap((Math.min.apply(null, S) + Math.max.apply(null, S)) / 2 - tw / 2, tw));
    else if (edge === 'vcenter') out.vo = r4(f.vo - wrap((Math.min.apply(null, T) + Math.max.apply(null, T)) / 2 - th / 2, th));
    else return null;
    return out;
  }
  /** Patch zum "Anmalen" einer Fläche mit aufgenommenen Attributen src (aus pick) von einer Fläche mit Normale nSrc.
   *  Nur die Textur, oder mit align auch Skala und Drehung; Achsen und Versatz nur bei gleich ausgerichteten Flächen. */
  function paintPatch(src: Picked, b: Brush, fi: number, nSrc: Vec3 | null | undefined, align?: boolean): FacePatch {
    const patch: FacePatch = { tex: src.tex };
    if (!align) return patch;
    patch.xs = src.xs; patch.ys = src.ys;
    if (nSrc && dot(faceNormal(b, fi), nSrc) > 0.99) { patch.ua = src.ua.slice() as Vec3; patch.va = src.va.slice() as Vec3; patch.uo = src.uo; patch.vo = src.vo; patch.rot = src.rot; }
    else Object.assign(patch, reset(b, fi), { xs: src.xs, ys: src.ys });
    return patch;
  }

  /** Welche Brushes/Flächen ein Klick beim Anmalen trifft: scope 'face' (nur diese Fläche), 'brush' (alle Flächen des Brushes) oder 'group'
   *  (alle Brushes der Gruppe, groupIds). Ohne Flächenindex fällt 'face' auf den Brush zurück. Liefert { ids: Set, only: [{b, f}] | null }. */
  function paintTarget(scope: string, id: string, fi: number | null | undefined, groupIds?: Set<string> | null): { ids: Set<string>; only: FaceRef[] | null } {
    if (scope === 'face' && fi !== null && fi !== undefined) return { ids: new Set([id]), only: [{ b: id, f: fi }] };
    if (scope === 'group' && groupIds && groupIds.size) return { ids: new Set(groupIds), only: null };
    return { ids: new Set([id]), only: null };
  }

  /** Attribute aufnehmen. */
  function pick(b: Brush, fi: number): Picked {
    const f = b.faces[fi];
    return { tex: f.tex, ua: f.ua.slice() as Vec3, uo: f.uo, va: f.va.slice() as Vec3, vo: f.vo, xs: f.xs, ys: f.ys, rot: f.rot };
  }
  /** Gemeinsame Kante zweier Flächen (zwei gleiche Eckpunkte) oder null. */
  function sharedEdge(bA: Brush, fA: number, bB: Brush, fB: number): [Vec3, Vec3] | null {
    const A = facePts(bA, fA), B = facePts(bB, fB), hit: Vec3[] = [];
    A.forEach((p) => { if (B.some((q) => Core.len(sub(p, q)) < 0.05)) hit.push(p); });
    if (hit.length < 2) return null;
    let best: [Vec3, Vec3] | null = null, bd = 0;
    for (let i = 0; i < hit.length; i++) for (let j = i + 1; j < hit.length; j++) {
      const d = Core.len(sub(hit[i], hit[j])); if (d > bd) { bd = d; best = [hit[i], hit[j]]; }
    }
    return bd > 0.5 ? best : null;
  }
  /** Textur von Fläche A (bA,fA) über die gemeinsame Kante auf B fortsetzen. Liefert Patch für B oder null. */
  function continueOver(bA: Brush, fA: number, bB: Brush, fB: number): FacePatch | null {
    const e = sharedEdge(bA, fA, bB, fB);
    if (!e) return null;
    const k = norm(sub(e[1], e[0])), nA = faceNormal(bA, fA), nB = faceNormal(bB, fB);
    const ang = Math.atan2(dot(cross(nA, nB), k), dot(nA, nB)) * 180 / Math.PI;
    const f = bA.faces[fA], ax = inPlane(f, nA);
    const ua = rotAbout(ax.ua, k, ang), va = rotAbout(ax.va, k, ang);
    const s0 = dot(e[0], ax.ua) / f.xs + f.uo, t0 = dot(e[0], ax.va) / f.ys + f.vo;
    return { tex: f.tex, ua: rv(ua), va: rv(va), xs: f.xs, ys: f.ys, uo: r4(s0 - dot(e[0], ua) / f.xs), vo: r4(t0 - dot(e[0], va) / f.ys), rot: f.rot };
  }

  /** Patch auf Flächen anwenden. targets: [{b: id, f: idx|null(alle)}], fn(brush, fi) -> Patch|null */
  function apply(doc: Doc, idSet: Set<string>, only: FaceRef[] | { b: string; f: number | null } | null | undefined, fn: (b: Brush, fi: number) => FacePatch | null): Doc {
    let ids = idSet;
    const list = Array.isArray(only) ? only : null, single = !Array.isArray(only) && only ? only : null;
    const keys = list ? new Set(list.map((x) => x.b + ':' + x.f)) : null;
    if (list) ids = new Set(list.map((x) => x.b));
    return Core.mapBrushes(doc, ids, (b) => {
      let ch = false;
      const faces = b.faces.map((f, i) => {
        if (keys) { if (!keys.has(b.id + ':' + i)) return f; }
        else {
          if (single && single.f !== null && single.b === b.id && single.f !== i) return f;
          if (single && single.f !== null && single.b !== b.id) return f;
        }
        const p = fn(b, i);
        if (!p) return f;
        ch = true; return Object.assign({}, f, p);
      });
      return ch ? Object.assign({}, b, { srcText: undefined, faces: faces }) : b;
    });
  }

  /** Beschreibung der Flächenrichtung. */
  function dirName(n: Vec3): string {
    const a = [Math.abs(n[0]), Math.abs(n[1]), Math.abs(n[2])], m = Math.max(a[0], a[1], a[2]);
    if (m < 0.9) return _('ta.slanted');
    return a[2] === m ? (n[2] > 0 ? _('ta.up') : _('ta.down')) : a[0] === m ? (n[0] > 0 ? '+X' : '−X') : (n[1] > 0 ? '+Y' : '−Y');
  }

  return { rotAbout, reset, fit, rotate, flipU, flipV, shift, scale, snapOrigin, alignEdge, paintPatch, paintTarget, pick, sharedEdge, continueOver, apply, dirName, inPlane };
})(Core);
export default TexAlign;
